import {
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import React from "react";
import { ToggleButtonGroup } from "../../components/ToggleButtonGroup";
import { CheckmarkCircleOutlineIcon } from "../../styles/icons";
import { Intent, templates, toneLabels } from "./templateData";

interface UseTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (message: string) => void;
}

export const UseTemplateModal: React.FC<UseTemplateModalProps> = ({
  isOpen,
  onClose,
  onApply,
}) => {
  const [selectedIntent, setSelectedIntent] =
    React.useState<Intent>("reminder");
  const [selectedMessage, setSelectedMessage] = React.useState<string | null>(
    null
  );
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = React.useState(false);
  const [showRightScroll, setShowRightScroll] = React.useState(true);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedIntent("reminder");
      setSelectedMessage(null);
    }
  }, [isOpen]);

  React.useEffect(() => {
    scrollContainerRef.current?.scrollTo({ left: 0, behavior: "auto" });
    setShowLeftScroll(false);
    setShowRightScroll(true);
  }, [selectedIntent, isOpen]);

  const intentTemplates = templates.filter((t) => t.intent === selectedIntent);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setShowLeftScroll(container.scrollLeft > 0);
    setShowRightScroll(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    );
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 260;
    const newScrollLeft =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  const handleApply = () => {
    if (selectedMessage) {
      onApply(selectedMessage);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Use Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack w="100%" alignItems="flex-start" spacing={4}>
            <Text fontSize="sm" fontWeight="bold">
              Select Intent
            </Text>
            <ToggleButtonGroup
              onChange={(v) => setSelectedIntent(v as Intent)}
              value={selectedIntent}
            >
              <Button value="reminder">Reminder</Button>
              <Button value="update">Update</Button>
              <Button value="engagement">Engagement</Button>
              <Button value="action">Action Required</Button>
              <Button value="information">Information</Button>
            </ToggleButtonGroup>

            <Box w="100%" position="relative">
              {showLeftScroll && (
                <IconButton
                  aria-label="Scroll left"
                  icon={<ChevronLeftIcon />}
                  onClick={() => scroll("left")}
                  size="sm"
                  borderRadius="full"
                  variant="outline"
                  colorScheme="gray"
                  bg="white"
                  position="absolute"
                  left={2}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={1}
                  boxShadow="sm"
                />
              )}

              <Box
                ref={scrollContainerRef}
                onScroll={handleScroll}
                overflowX="auto"
                w="100%"
                css={{
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  scrollBehavior: "smooth",
                }}
              >
                <HStack spacing={4} pb={2}>
                  {intentTemplates.map((template) => {
                    const isSelected = selectedMessage === template.message;
                    return (
                      <Box
                        key={template.tone}
                        border="1px solid"
                        borderColor={
                          isSelected ? "cherryButton.500" : "gray.200"
                        }
                        borderRadius="md"
                        p={4}
                        bg="cherryBackground.tile"
                        minW="280px"
                        maxW="280px"
                        height="200px"
                        flexShrink={0}
                        display="flex"
                        flexDirection="column"
                      >
                        <VStack alignItems="flex-start" spacing={3} flex="1">
                          <Text fontSize="sm" fontWeight="bold">
                            {toneLabels[template.tone]}
                          </Text>
                          <Text fontSize="sm" color="gray.700" flex="1">
                            {template.message}
                          </Text>
                          <Button
                            size="sm"
                            onClick={() => setSelectedMessage(template.message)}
                            bg={isSelected ? "cherryButton.25" : "white"}
                            color="black"
                            borderColor={
                              isSelected ? "cherryButton.500" : "gray.300"
                            }
                            borderWidth="1px"
                            leftIcon={<CheckmarkCircleOutlineIcon />}
                            justifyContent="flex-start"
                            _hover={{ bg: "gray.50" }}
                            _active={{ bg: "gray.100" }}
                          >
                            {isSelected ? "Selected" : "Use template"}
                          </Button>
                        </VStack>
                      </Box>
                    );
                  })}
                </HStack>
              </Box>

              {showRightScroll && (
                <IconButton
                  aria-label="Scroll right"
                  icon={<ChevronRightIcon />}
                  onClick={() => scroll("right")}
                  size="sm"
                  borderRadius="full"
                  variant="outline"
                  colorScheme="gray"
                  bg="white"
                  position="absolute"
                  right={2}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={1}
                  boxShadow="sm"
                />
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="cherry-outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="cherryButton"
            onClick={handleApply}
            isDisabled={!selectedMessage}
            ml={3}
          >
            Apply Message
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
